import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { handleAppMetaTag } from '@/lib/utils/handleAppMetaTag';
import { useUser } from '@/queries/useUser';

/**
 * Route configuration for Apple Smart App Banner
 * Add routes that should display the app install banner
 */
const ROUTES_WITH_APP_BANNER: string[] = [
    '/dashboard',
    '/stores',
    '/income',
    '/analytics',
    '/audience',
    '/flows',
    // Add more routes as needed
];

/**
 * Custom hook to manage Apple Smart App Banner meta tag on route changes
 * Automatically adds/removes the meta tag based on route configuration and user experiments
 *
 * @example
 * ```tsx
 * function App() {
 *   useAppMetaTag();
 *   return <Routes>...</Routes>;
 * }
 * ```
 */
export function useAppMetaTag() {
    const location = useLocation();
    const { user } = useUser();

    useEffect(() => {
        // Check if current route should show the app banner
        const shouldShowBanner = ROUTES_WITH_APP_BANNER.some(route => location.pathname.startsWith(route));

        handleAppMetaTag(shouldShowBanner, user);
    }, [location.pathname, user]);
}

/**
 * Check if a specific route should show the app banner
 * @param pathname - The route pathname to check
 * @returns boolean indicating if the banner should show
 */
export function shouldShowAppBanner(pathname: string): boolean {
    return ROUTES_WITH_APP_BANNER.some(route => pathname.startsWith(route));
}
